import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useEffect, useRef, useState, useCallback } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useParams } from "react-router-dom";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import {
  postMessage,
  getMessages,
  clearMessages,
  retryMessage,
} from "../utils/Services";
import { client } from "../utils/AxiosClient";
import NoteDropdown from "../components/NoteDropdown";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  StarFour,
  PaperPlaneRight,
  Microphone,
  X,
  ClockCounterClockwise,
  Check,
  ArrowCounterClockwise,
  Pause,
  Info,
  Notepad,
} from "@phosphor-icons/react";
import { sendAudioToS3, audioProcessed } from "@/utils/Services";
import { Message, INote } from "@/utils/Types";
import { debounce } from "lodash";
import { updateNoteFieldById } from "../utils/Services";
import { useForm } from "react-hook-form";
import useIsMobile from "@/hooks/useIsMobile";

interface ConfirmationModalProps {
  onClose: () => void;
  isLoad: boolean;
}

const Copilot: React.FC<ConfirmationModalProps> = ({ onClose, isLoad }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const socket = useRef<WebSocket | null>(null);
  const [socketResponse, setSocketResponse] = useState(
    {} as {
      status: string;
      object: Message;
    }
  );
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(50);
  const [messagesCount, setMessagesCount] = useState(0);
  const [newMessagesCount, setNewMessagesCount] = useState(0);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isCancel = useRef<boolean>(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const recordingIntervalRef = useRef<number | null>(null);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const { height, width } = useWindowDimensions();
  const [retryMsg, setRetryMsg] = useState({} as Message);
  const contentRef = useRef(null);
  const textareaRef: any = useRef(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const buffer = useRef<number[]>(new Array(64).fill(0));
  const animationTimeoutRef = useRef<number | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastUpdateTime = useRef<number>(0);
  const isMobile = useIsMobile();

  const { noteId = "", folder = "" } = useParams<{
    folder: string;
    noteId: string;
  }>();

  const [activeTab, setActiveTab] = useState("chat");
  const [note, setNote] = useState<INote>({} as INote);
  const canUpdate = useRef(false);

  const toggleSection = (msg: Message) => {
    setMessages(
      messages.map((message) =>
        message.id === msg?.id
          ? {
              ...message,
              collapsible: msg?.collapsible,
            }
          : message
      )
    );
  };

  const handleKey = (e: KeyboardEvent): void => {
    const { ctrlKey, key, shiftKey, target } = e;

    if (
      target instanceof HTMLElement &&
      target.id !== "copilot-textarea" &&
      (target.tagName === "INPUT" || target.tagName === "TEXTAREA")
    ) {
      return;
    }

    if ((ctrlKey && key === " ") || (shiftKey && ctrlKey && key === " ")) {
      if (!isRecording) {
        e.preventDefault();
        startRecording();
      } else {
        e.preventDefault();
        togglePauseRecording();
      }
    } else if (ctrlKey && key === "Enter") {
      e.preventDefault();
      stopRecording();
    } else if (key === "Escape") {
      e.preventDefault();
      cancelRecording();
    }
    if (!ctrlKey && key === "Enter") {
      if (shiftKey) {
        e.preventDefault();
        setInputText((prev) => `${prev}\n`);
        autoResize(true);
      } else {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const autoResize = (forceExpand = false) => {
    const textArea: any = textareaRef.current;
    if (textArea) {
      textArea.style.height = "auto";
      textArea.style.height =
        textArea.scrollHeight + (forceExpand ? 20 : 0) + "px";
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKey);

    return () => {
      window.removeEventListener("keydown", handleKey);
    };
  }, [handleKey]);

  const fetchMessages = async (
    noteId: string,
    replace: boolean = false,
    off = 0
  ) => {
    setIsLoading(true);
    try {
      const response = await getMessages({
        note_id: noteId,
        offset: off,
        limit: limit,
      });
      setMessagesCount(response.count);

      if (replace) {
        setMessages([
          ...response.results,
          ...(!response.count
            ? [
                {
                  id: Date.now().toString(),
                  text: "Create a new note by choosing either the text or audio option",
                  type: "bot",
                  from_audio: false,
                },
              ]
            : []),
        ]);
      } else {
        setMessages([
          ...messages,
          ...response.results,
          ...(!response.count
            ? [
                {
                  id: Date.now().toString(),
                  text: "Create a new note by choosing either the text or audio option",
                  type: "bot",
                  from_audio: false,
                },
              ]
            : []),
        ]);
      }
    } catch (error) {
      console.error("Failed to load profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (messagesCount && messagesCount === messages.length) {
      setMessages([
        ...messages,
        {
          id: Date.now().toString(),
          text: "Create a new note by choosing either the text or audio option",
          type: "bot",
          from_audio: false,
        },
      ]);
    }
  }, [messages]);

  const loadMoreMessages = () => {
    if (messagesCount > messages.length) {
      setOffset(offset + limit + newMessagesCount);
      fetchMessages(noteId, false, offset + limit + newMessagesCount);
      setNewMessagesCount(0);
    }
  };

  useEffect(() => {
    return () => {
      if (socket.current) {
        socket.current.close();
        socket.current = null;
      }
    };
  }, [noteId]);

  useEffect(() => {
    if (noteId) {
      setNewMessagesCount(0);
      setMessagesCount(0);
      setInputText("");
      setOffset(0);
      setMessages([]);
      const res = setTimeout(() => {
        fetchMessages(noteId, true);
      }, 500);

      return () => {
        clearTimeout(res);
        if (socket.current) {
          socket.current.close();
          socket.current = null;
        }
      };
    }
  }, [noteId]);

  useEffect(() => {
    const openSocket = async () => {
      try {
        const {
          data: { uuid: uuidToken },
        } = await client.post(`${import.meta.env.VITE_SOCKET_AUTH_URL}`, {
          note_id: noteId,
        });

        socket.current = new WebSocket(
          `${import.meta.env.VITE_SOCKET_URL_MESSAGE}?uuid=${uuidToken}`
        );

        socket.current.onerror = function (event) {};

        socket.current.onclose = function (event) {
          if (event.wasClean) {
          } else {
            // e.g. server process killed or network down
          }
        };

        socket.current.onopen = () => {};
      } catch (error: any) {
        if (error.response) {
          // The request was made and the server responded with a status code
        } else if (error.request) {
          // The request was made but no response was received
        } else {
          // Something happened in setting up the request that triggered an Error
        }
        console.error("Error opening WebSocket:", error);
      }
    };

    const res = setTimeout(() => {
      if (noteId) {
        openSocket();
      }
    }, 1000);

    return () => {
      clearTimeout(res);

      if (socket.current) {
        socket.current.close();
        socket.current = null;
      }
    };
  }, [noteId]);

  useEffect(() => {
    if (socketResponse?.status === "created") {
      setMessages([socketResponse?.object, ...messages]);
      setNewMessagesCount(newMessagesCount + 1);
    } else {
      setMessages(
        messages.map((message) =>
          message.id === socketResponse?.object?.id
            ? {
                ...message,
                ...socketResponse?.object,
                ...(socketResponse?.status === "transcribed" && {
                  transcribed: true,
                }),
              }
            : message
        )
      );
    }
  }, [socketResponse]);

  useEffect(() => {
    if (socket.current) {
      socket.current.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);
          setSocketResponse(message.message);
        } catch (error) {
          console.error("Error while parsing socket message:", error);
        }
      };
    }
  }, [socket.current]);

  useEffect(() => {
    return () => clearInterval(recordingIntervalRef.current!);
  }, []);

  const handleSend = async () => {
    if (!inputText.trim()) return;
    setIsUploading(true);

    textareaRef.current.style.height = "auto";

    if (inputText.trim()) {
      const newMessage = {
        id: Date.now().toString(),
        text: inputText,
        type: "user",
      };

      try {
        await postMessage(noteId, {
          text: newMessage.text,
        });

        setInputText("");
      } catch (error) {
        console.error("Failed to upload audio:", error);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleSendAudio = async (blob: Blob) => {
    setIsUploading(true);
    setInputText("");

    try {
      const res = await postMessage(noteId, {
        from_audio: true,
      });
      setIsUploading(false);

      await sendAudioToS3(res.l!.url, res.l!.fields, blob, "audio/mp3");

      await audioProcessed(res.response!.id);
    } catch (error) {
      console.error("Error uploading audio:", error);
      setIsUploading(false);
    } finally {
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds
    ).padStart(2, "0")}`;
  };

  const visualizeBars = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const renderBars = (timestamp: number) => {
      if (!analyserRef.current || !dataArrayRef.current || isPaused) {
        return;
      }

      if (timestamp - lastUpdateTime.current < 50) {
        animationFrameRef.current = requestAnimationFrame(renderBars);
        return;
      }
      lastUpdateTime.current = timestamp;

      analyserRef.current.getByteFrequencyData(dataArrayRef.current);

      buffer.current.push(
        dataArrayRef.current[4] * 0.5 +
          buffer.current[buffer.current.length - 1] * 0.5
      );
      if (buffer.current.length > 64) buffer.current.shift();

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const barWidth = (canvas.width / buffer.current.length) * 0.8;
      const centerY = canvas.height / 2;
      let x = (canvas.width - buffer.current.length * (barWidth + 2)) / 2;

      buffer.current.forEach((barHeight) => {
        const height = (barHeight / 255) * canvas.height * 0.9;

        ctx.fillStyle = "#6b7280";
        ctx.beginPath();
        ctx.roundRect(x, centerY - height / 2, barWidth, height, 8);
        ctx.fill();

        x += barWidth + 3;
      });

      animationFrameRef.current = requestAnimationFrame(renderBars);
    };

    animationFrameRef.current = requestAnimationFrame(renderBars);
  };

  const startRecording = async () => {
    if (isPaused) {
      togglePauseRecording();
      return;
    }

    setIsRecording(true);
    setRecordingTime(0);
    audioChunksRef.current = [];

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const audioContext = new (window.AudioContext ||
      (window as any).webkitAudioContext)();

    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }

    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 128;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    source.connect(analyser);

    audioContextRef.current = audioContext;
    analyserRef.current = analyser;
    dataArrayRef.current = dataArray;

    visualizeBars();

    mediaRecorderRef.current = new MediaRecorder(stream);

    mediaRecorderRef.current.ondataavailable = (e) => {
      audioChunksRef.current.push(e.data);
    };

    mediaRecorderRef.current.onstop = () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/mp3" });

      if (!isCancel.current && audioChunksRef.current.length) {
        handleSendAudio(audioBlob);
      } else {
        isCancel.current = false;
      }
      stream.getAudioTracks()[0].stop();

      clearInterval(recordingIntervalRef.current!);
      cancelAnimationFrame(animationFrameRef.current!);
    };

    mediaRecorderRef.current.start();

    recordingIntervalRef.current = window.setInterval(() => {
      setRecordingTime((prevTime) => prevTime + 1);
    }, 1000);
  };

  const drawEmptyWaveform = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecordingTime(0);
    setIsRecording(false);
    setIsPaused(false);
    audioChunksRef.current = [];
    buffer.current = new Array(64).fill(0);
    cancelAnimationFrame(animationFrameRef.current!);
    drawEmptyWaveform();
  };

  const cancelRecording = () => {
    isCancel.current = true;
    setRecordingTime(0);
    setIsRecording(false);
    setIsPaused(false);
    audioChunksRef.current = [];
    mediaRecorderRef.current?.stop();
    clearInterval(recordingIntervalRef.current!);
    buffer.current = new Array(64).fill(0);
    cancelAnimationFrame(animationFrameRef.current!);
    drawEmptyWaveform();
  };

  useEffect(() => {
    if (!isPaused) {
      visualizeBars();
    } else {
      cancelAnimationFrame(animationFrameRef.current!);
    }
  }, [isPaused]);

  const togglePauseRecording = () => {
    if (mediaRecorderRef.current) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
        setIsPaused(false);
        setIsRecording(true);
        recordingIntervalRef.current = window.setInterval(() => {
          setRecordingTime((prevTime) => prevTime + 1);
        }, 1000);
      } else {
        mediaRecorderRef.current.pause();
        setIsPaused(true);
        setIsRecording(false);
        clearInterval(recordingIntervalRef.current!);
      }
    }
  };

  const handleInputChange = (e: any) => {
    setInputText(e.target.value);
    autoResize();
  };

  const clearCopilot = async () => {
    try {
      await clearMessages(noteId);
      fetchMessages(noteId, true);
    } catch (error) {}
  };

  const retry = async (id: string) => {
    try {
      await retryMessage(id, {});
    } catch (error) {}
  };

  const dropdownItems = [
    {
      title: "Clear Messages",
      href: "#",
      icon: <X size={20} />,
      onClick: clearCopilot,
    },
  ];

  const msgDropdownItems = [
    {
      title: "Retry",
      href: "#",
      icon: <ArrowCounterClockwise size={20} />,
      onClick: () => retry(retryMsg?.id),
    },
  ];

  const isAnyMessageInProcess = () => {
    const userMsgs = messages.filter((msg) => msg.type === "user");
    return userMsgs[0]?.processing_status === "STARTING";
  };

  const values = note;

  const {
    register,
    watch,
    formState: { errors },
  } = useForm<INote>({ values });

  const autoSave = (value: INote) => {
    const effectiveId = value.id || noteId;
    if (!effectiveId) {
      console.error("Missing note id. Cannot update scratchpad.");
      return;
    }

    if (!canUpdate.current) {
      canUpdate.current = true;
      return;
    }

    updateNoteFieldById("notes", effectiveId, {
      scratchpad: value.scratchpad,
    });
    canUpdate.current = false;
  };

  const debounceFn = useCallback(debounce(autoSave, 1000), []);

  useEffect(() => {
    const subscription = watch((value) => {
      // @ts-ignore
      debounceFn(value);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    canUpdate.current = false;
  }, [note]);

  return (
    <div
      className={
        isMobile
          ? "bg-white dark:bg-gray-950 w-full relative"
          : "bg-white dark:bg-gray-950 w-full relative"
      }
    >
      <Tabs
        defaultValue="chat"
        className="w-full h-full"
        onValueChange={setActiveTab}
      >
        <TabsList
          className="grid p-0 w-full grid-cols-2 h-14 bg-gray-25/20 dark:bg-gray-800/50 border-b-[0.5px]"
          style={{
            borderRadius: "0px",
          }}
        >
          <TabsTrigger
            value="chat"
            style={{
              borderRadius: "0px",
            }}
            className="border-b-[1px] bg-gray-25/20 dark:bg-gray-800/50 h-full transition-all text-gray-950 dark:text-white hover:bg-gray-25 dark:hover:bg-gray-800/50 text-sm font-normal data-[state=active]:text-gray-800 dark:data-[state=active]:text-white dark:data-[state=active]:bg-sky-800/10 data-[state=active]:bg-sky-25/20 data-[state=active]:border-b-[1px] data-[state=active]:border-sky-200 dark:data-[state=active]:border-sky-200"
          >
            <div className="flex items-center">
              <div className="text-md font-medium flex items-center">
                <div className="flex items-center justify-center bg-sky-200 dark:bg-sky-200 h-[18px] w-[18px] rounded-full">
                  <StarFour className="text-sky-25" size={14} weight="fill" />
                </div>
                <div className="ml-2">Copilot</div>
              </div>
            </div>
          </TabsTrigger>

          <TabsTrigger
            value="scratchpad"
            style={{
              borderRadius: "0px",
            }}
            className="border-b-[1px] bg-gray-25/20 dark:bg-gray-800/50 h-full transition-all data-[state=active]:text-gray-800 dark:data-[state=active]:text-white
                text-gray-600 dark:text-white hover:bg-gray-25 dark:hover:bg-gray-800/50 text-sm font-normal data-[state=active]:border-b-[1px] data-[state=active]:border-sky-200 dark:data-[state=active]:border-sky-200 dark:data-[state=active]:bg-sky-800/10 data-[state=active]:bg-sky-25/20"
          >
            <Notepad size={20} />
            <div className="text-md font-medium ml-2">Scratchpad</div>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chat" className="h-full">
          <div id="scroll" className="flex flex-col copilot-height">
            {isLoad ? (
              <div
                className="w-full py-10 text-center flex items-center justify-center p-2"
                style={{
                  height: `calc(100vh - ${isMobile ? 130 : 160}px)`,
                }}
              >
                <span className="loader w-5 h-5"></span>
              </div>
            ) : (
              <div
                style={{
                  height: `calc(100dvh - ${isMobile ? 130 : 160}px)`,
                }}
              >
                <InfiniteScroll
                  dataLength={messages.length}
                  next={loadMoreMessages}
                  hasMore={messagesCount > messages.length}
                  loader={
                    <div className="w-full text-center p-2">
                      <span className="loader w-5 h-5"></span>
                    </div>
                  }
                  style={{
                    display: "flex",
                    flexDirection: "column-reverse",
                  }}
                  height={"calc(100dvh - 220px)"}
                  inverse={true}
                  scrollableTarget="scroll"
                >
                  {messages.length > 1 && isAnyMessageInProcess() && (
                    <div className="mx-2 mt-2 mb-4 flex text-sm p-[17px]">
                      <div className="flex items-center justify-center bg-sky-200 h-[20px] w-[20px] rounded-full">
                        <StarFour
                          className="text-sky-25 mx-1"
                          size={15}
                          weight="fill"
                        />
                      </div>
                      <div className="ml-2 flex items-center text-gray-800 dark:text-white">
                        <div className="flex">
                          {[1, 2, 3].map((index) => (
                            <span
                              key={index}
                              className="mr-1 h-2 w-2 rounded-full bg-sky-200 dark:bg-sky-50 blinking"
                            ></span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {[...messages].map((message) =>
                    message.type === "user" ? (
                      <div
                        className="mx-2 flex"
                        id={message.id}
                        key={message.id}
                      >
                        <div
                          className={
                            "relative ml-auto mb-2 max-w-[100%] " +
                            (message.processing_status === "ERROR"
                              ? "min-w-[100px]"
                              : "")
                          }
                        >
                          {message.processing_status === "ERROR" && (
                            <>
                              <Info
                                size={30}
                                className="h-[24px] absolute top-2 left-2 text-red-500"
                              />

                              <div className="absolute top-2 right-2">
                                <Button
                                  onClick={() => {
                                    retry(message?.id);
                                  }}
                                  className="flex items-center justify-center rounded-full bg-gray-600 hover:bg-gray-25 h-[24px] text-xs"
                                >
                                  <ArrowCounterClockwise
                                    className="text-gray-50 mr-1"
                                    size={15}
                                  />
                                  <span className="text-gray-50">Retry</span>
                                </Button>
                              </div>
                            </>
                          )}
                          <div
                            className={
                              "shadow-md group rounded-l-[20px] rounded-br-[20px] flex text-sm text-gray-950 dark:text-white py-3 pl-4 " +
                              (message.processing_status === "ERROR"
                                ? "border-2 border-red-500 pt-10 pr-4"
                                : "bg-sky-25/20 border-[0.5px] border-sky-25/20 dark:bg-gray-600 dark:border-gray-500")
                            }
                          >
                            <div className="flex ">
                              {!message.from_audio ? (
                                <span className="content-end">
                                  {message.text}
                                </span>
                              ) : (
                                <div
                                  onClick={() => {
                                    toggleSection({
                                      ...message,
                                      collapsible: message.collapsible
                                        ? ""
                                        : message.id,
                                    });
                                  }}
                                  className={
                                    "cursor-pointer flex max-w-[290px] transition-all duration-300 overflow-hidden"
                                  }
                                  style={{
                                    maxHeight: !!message.collapsible
                                      ? 10000
                                      : 24,
                                  }}
                                >
                                  <span className="waveform mr-2">
                                    <span className="bar"></span>
                                    <span className="bar"></span>
                                    <span className="bar"></span>
                                    <span className="bar"></span>
                                    <span className="bar"></span>
                                  </span>
                                  {message.processing_status !== "COMPLETE" && (
                                    <span className="mr-2">
                                      {!message.transcribed && (
                                        <span className="text-sky-200 dark:text-sky-50">
                                          {message.audio_status !== "COMPLETE"
                                            ? "Uploading..."
                                            : "Transcribing..."}
                                        </span>
                                      )}
                                    </span>
                                  )}

                                  <div
                                    ref={contentRef.current}
                                    className={
                                      !message.collapsible ? "truncate" : ""
                                    }
                                  >
                                    {message.text}
                                  </div>
                                </div>
                              )}
                            </div>
                            {message.processing_status !== "ERROR" && (
                              <div
                                onClick={() => setRetryMsg(message)}
                                className="h-5 w-5 cursor-pointer opacity-0 group-hover:opacity-100"
                              >
                                <NoteDropdown
                                  size="h-5 w-5"
                                  contentW="w-[100px]"
                                  items={msgDropdownItems}
                                />
                              </div>
                            )}
                          </div>
                          <div className="flex items-center mt-1 text-gray-500 dark:text-gray-100 text-xs">
                            <span className="ml-auto mr-1">
                              {message?.processing_status !== "COMPLETE" ? (
                                <ClockCounterClockwise size={14} />
                              ) : (
                                <Check size={14} />
                              )}
                            </span>
                            {new Date(message?.created!).toLocaleTimeString(
                              [],
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div
                        id={message.id}
                        key={message.id}
                        className="mx-2 mt-2 mb-2 rounded-[6px] flex text-sm p-[17px]"
                      >
                        <div className="flex items-center justify-center bg-sky-200 dark:bg-sky-200 h-[20px] w-[20px] rounded-full">
                          <StarFour
                            className="text-sky-25 dark:text-sky-25 mx-1"
                            size={15}
                            weight="fill"
                          />
                        </div>
                        <div className="ml-2 flex items-center text-gray-800 dark:text-white">
                          {message.text}
                        </div>
                      </div>
                    )
                  )}
                </InfiniteScroll>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="scratchpad" className="h-full px-4 py-2">
          <div
            style={{
              height: `calc(100dvh - ${isMobile ? 140 : 160}px)`,
            }}
          >
            <div className="flex items-center mb-2">
              <Info className="mr-1" size={16} />
              <span className="text-xs">
                It will not be included in the final note.
              </span>
            </div>
            <Textarea
              id="scratchpad"
              placeholder="Start typing your notes..."
              className="w-full h-[calc(100vh-210px)] resize-none overflow-y-auto rounded-lg border border-gray-25 dark:border-gray-600 bg-white dark:bg-gray-800/20 text-gray-900 dark:text-white px-4 py-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-400"
              {...register("scratchpad")}
            />
          </div>
        </TabsContent>
      </Tabs>

      {activeTab === "chat" && (
        <div className="shadow-lg ml-2 w-[97%] h-[50px] absolute bottom-4 py-2 px-1 border rounded-3xl border-gray-25 bg-white dark:border-gray-700 dark:bg-gray-800 flex flex-col items-center justify-center">
          {(isPaused || isRecording) && (
            <div className="w-full mt-auto flex items-center justify-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipContent
                    side="bottom"
                    className="bg-gray-600 text-white"
                  >
                    <p>Press esc to cancel the recording</p>
                  </TooltipContent>
                  <TooltipTrigger asChild>
                    <Button
                      disabled={isUploading || isLoad || isLoading}
                      onClick={cancelRecording}
                      className="ml-2 p-2 bg-gray-25 h-[36px] w-[36px] rounded-full hover:bg-gray-25/50 dark:hover:bg-gray-500 dark:bg-gray-400"
                    >
                      <X className="text-gray-500 dark:text-white" size={32} />
                    </Button>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>

              <div className="w-full flex items-center justify-center">
                {isRecording && !isPaused && (
                  <div className="flex h-3 w-3 items-center">
                    <span className="animate-pulse bg-red-600 dark:bg-red-400 rounded-full h-3 w-3"></span>
                  </div>
                )}
                {!isRecording && isPaused && (
                  <div className="flex h-3 w-3 items-center">
                    <span className="bg-yellow-400 dark:bg-yellow-500 rounded-full h-3 w-3"></span>
                  </div>
                )}
                <div
                  className="text-gray-500 dark:text-white font-semibold ml-2"
                  style={{
                    fontSize: 17,
                  }}
                >
                  {isPaused ? "Paused" : formatTime(recordingTime)}
                </div>
              </div>

              {/* <div className="w-full mx-2">
                <canvas ref={canvasRef} className="w-full" height={36}></canvas>
              </div> */}

              <TooltipProvider>
                <Tooltip>
                  <TooltipContent
                    side="bottom"
                    className="bg-gray-600 text-white"
                  >
                    <p>Press ctrl + enter to send the recording</p>
                  </TooltipContent>
                  <TooltipTrigger asChild>
                    <Button
                      disabled={isUploading || isLoad || isLoading}
                      onClick={stopRecording}
                      className="mr-2 h-[36px] w-[36px] ml-auto bg-sky-200 p-2 rounded-full hover:bg-sky-100"
                    >
                      <PaperPlaneRight
                        className="text-sky-25"
                        size={32}
                        weight="fill"
                      />
                    </Button>
                  </TooltipTrigger>
                </Tooltip>
              </TooltipProvider>

              <Button
                onClick={togglePauseRecording}
                className={
                  "h-[36px] w-[36px] p-2 rounded-full mr-2 " +
                  (!isPaused
                    ? "bg-red-100 hover:bg-red-200"
                    : "bg-green-300 hover:bg-green-200")
                }
              >
                {isPaused ? (
                  <Microphone
                    className="text-green-800"
                    weight="fill"
                    size={32}
                  />
                ) : (
                  <Pause className="text-red-500" weight="fill" size={32} />
                )}
              </Button>
            </div>
          )}
          {!isRecording && !isPaused && (
            <div className="flex w-full max-h-[200px] relative">
              <Textarea
                id="copilot-textarea"
                disabled={isUploading || isLoad || isLoading}
                rows={1}
                placeholder="Type a message..."
                value={inputText}
                onInput={handleInputChange}
                ref={textareaRef}
                className="mr-10 border-0 bg-white dark:bg-gray-800 min-h-[20px] max-h-[200px]"
                style={{ resize: "none" }}
              />
              {!inputText && (
                <div className="flex items-center justify-center rounded-full absolute right-14 bottom-2">
                  <NoteDropdown
                    gear
                    contentW="w-[180px]"
                    items={dropdownItems}
                  />
                </div>
              )}
              <Button
                disabled={isUploading || isLoad || isLoading}
                onClick={() => (inputText ? handleSend() : startRecording())}
                className="h-[36px] w-[36px] absolute right-0 bottom-0 bg-sky-200 p-2 rounded-full mr-2 hover:bg-sky-100"
              >
                {inputText ? (
                  <PaperPlaneRight
                    className="text-sky-25"
                    size={32}
                    weight="fill"
                  />
                ) : (
                  <Microphone className="text-sky-25" weight="fill" size={32} />
                )}
              </Button>
            </div>
          )}
        </div>
      )}
      {/* <RetryModal
        message={retryMsg}
        isOpen={isOpen}
        isError={retryMsg?.processing_status === "ERROR"}
        setIsOpen={setIsOpen}
      /> */}
    </div>
  );
};

export default Copilot;
